import type Phaser from 'phaser';
import { PET, TARGETING_TICK_MS } from '@/config/balance.config';
import type { Pet } from '@/game/entities/Pet';
import type { PetTarget } from '@/game/entities/PetTarget';
import type { Player } from '@/game/entities/Player';

/**
 * Co TARGETING_TICK_MS przypisuje wolnym petom najbliższego destructibla
 * w promieniu PET.agroRange od gracza.
 *
 * Reguły:
 *  - Bierzemy tylko żywe destructibles z wolnymi slotami (def.maxPetSlots).
 *  - Każdy wolny pet (state === IDLE_FOLLOW) dostaje najbliższy obiekt.
 *  - Sortujemy destructibles po odległości od GRACZA, nie od peta — żeby cała
 *    drużyna skupiała się na tych samych obiektach (bardziej "intuicyjne").
 */
export class TargetingSystem {
  private timer: Phaser.Time.TimerEvent | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly player: Player,
    private readonly pets: Pet[],
    private readonly targets: PetTarget[],
  ) {}

  start(): void {
    this.timer?.remove();
    this.timer = this.scene.time.addEvent({
      delay: TARGETING_TICK_MS,
      loop: true,
      callback: () => this.tick(),
    });
    this.tick();
  }

  stop(): void {
    this.timer?.remove();
    this.timer = null;
  }

  private tick(): void {
    // Lista wolnych petów
    const idlePets = this.pets.filter((p) => p.isIdle());
    if (idlePets.length === 0) return;

    // Lista celów (destruktyble / boss / posągi) posortowana po dystansie od gracza
    const candidates = this.targets
      .filter((d) => d.hasFreeSlot())
      .map((d) => ({
        dest: d,
        dist: Math.hypot(d.x - this.player.x, d.y - this.player.y),
      }))
      .filter((c) => c.dist <= PET.agroRange)
      .sort((a, b) => a.dist - b.dist);

    if (candidates.length === 0) return;

    // Przypisz pety do najbliższych obiektów (każdy obiekt może wziąć
    // wielu petów, do maxPetSlots).
    for (const pet of idlePets) {
      for (const c of candidates) {
        if (!c.dest.hasFreeSlot()) continue;
        if (pet.assignTarget(c.dest)) break;
      }
    }
  }
}
